import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface User {
  id: number;
  name: string;
  email: string;
  isAdmin: boolean;
  photo?: string;
  photo_id?: string;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  login: () => void;
  logout: () => void;
  checkAuth: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isCheckingAuth, setIsCheckingAuth] = useState(false);

  const checkAuth = async () => {
    // Evitar múltiplas chamadas simultâneas
    if (isCheckingAuth) {
      return;
    }
    
    setIsCheckingAuth(true);
    
    try {
      // Verificar se há token na URL (após login)
      const urlParams = new URLSearchParams(window.location.search);
      const token = urlParams.get('token');
      
      console.log('🔍 Debug AuthContext - Token na URL:', token ? 'Sim' : 'Não');
      
      if (token) {
        // Salvar token no localStorage
        try {
          localStorage.setItem('authToken', token);
          console.log('✅ Token salvo no localStorage com sucesso');
        } catch (error) {
          console.error('❌ Erro ao salvar token no localStorage:', error);
        }
        
        // Limpar token da URL
        window.history.replaceState({}, document.title, window.location.pathname);
      }
      
      // Pegar token do localStorage
      const storedToken = localStorage.getItem('authToken');
      console.log('🔍 Debug AuthContext - Token no localStorage:', storedToken ? 'Sim' : 'Não');
      
      if (!storedToken) {
        console.log('❌ Nenhum token encontrado no localStorage');
        setUser(null);
        return;
      }
      
      console.log('🔍 Debug AuthContext - Fazendo requisição para /api/auth/me');
      console.log('🔍 Debug AuthContext - URL completa:', window.location.origin + '/api/auth/me');
      console.log('🔍 Debug AuthContext - Token sendo enviado:', storedToken ? storedToken.substring(0, 50) + '...' : 'Nenhum');
      
      // Verificar autenticação com JWT
      const response = await fetch('/api/auth/me', {
        headers: {
          'Authorization': `Bearer ${storedToken}`,
          'Content-Type': 'application/json'
        }
      });
      
      console.log('🔍 Debug AuthContext - Status da resposta:', response.status);
      
      // Log do conteúdo da resposta para debug
      const responseText = await response.text();
      console.log('🔍 Debug AuthContext - Conteúdo da resposta:', responseText.substring(0, 200));
      
      if (response.ok) {
        let userData;
        try {
          userData = JSON.parse(responseText);
        } catch (error) {
          console.log('❌ Erro ao fazer parse do JSON:', error);
          console.log('❌ Conteúdo completo da resposta:', responseText);
          setUser(null);
          localStorage.removeItem('authToken');
          return;
        }
        console.log('🔍 Debug AuthContext - Dados do usuário:', userData);
        
        // Verificar se a resposta contém dados válidos de usuário
        if (userData.error || userData.message || !userData.id || !userData.name || !userData.email) {
          console.log('❌ Dados de usuário inválidos:', userData);
          setUser(null);
          localStorage.removeItem('authToken');
          return;
        }
        
        const user: User = {
          id: userData.id,
          name: userData.name,
          email: userData.email,
          isAdmin: userData.role === 'admin',
          photo: userData.photo_url,
          photo_id: userData.photo_id
        };
        console.log('✅ Usuário autenticado com sucesso:', user);
        setUser(user);
      } else {
        console.log('❌ Erro na resposta da API:', response.status, response.statusText);
        setUser(null);
        localStorage.removeItem('authToken');
      }
    } catch (error) {
      console.error('❌ Erro ao verificar autenticação:', error);
      setUser(null);
      localStorage.removeItem('authToken');
      // Não definir erro para evitar loops
    } finally {
      setIsLoading(false);
      setIsCheckingAuth(false);
    }
  };

  const login = () => {
    // Limpar erros anteriores e definir loading
    setError(null);
    setIsLoading(true);
    
    // Usar a origem atual (funciona em produção sem depender do build)
    const backendUrl =
      typeof window !== 'undefined' && window.location.origin
        ? window.location.origin
        : (process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001');
    window.location.href = `${backendUrl}/auth/google`;
  };

  const logout = async () => {
    try {
      // Limpar token do localStorage
      localStorage.removeItem('authToken');
      
      // Limpar estado local
      setUser(null);
      
      // Redirecionar para login
      window.location.href = '/login';
    } catch (error) {
      console.error('Erro ao fazer logout:', error);
      // Fallback: limpar estado local
      setUser(null);
      window.location.href = '/login';
    }
  };

  useEffect(() => {
    checkAuth();
  }, []);

  // Verificar token na URL quando a página carrega
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    
    if (token) {
      checkAuth();
    }
  }, []);

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    error,
    login,
    logout,
    checkAuth
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
